import React, { FC, useCallback, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { Loader, PublicError } from '@modernloop/shared/helper-components';
import { FormControlLabel, Link, Stack, Switch, TextField, Typography } from '@mui/material';

import { AtsType } from 'src/generated/mloop-graphql';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import useAshbyIntegration from 'src/hooks/api/integration/useAshbyIntegration';
import useAshbyIntegrationPut from 'src/hooks/api/integration/useAshbyIntegrationPut';
import { getAtsService } from 'src/hooks/atsService';

export interface Props {
  apiKey: string;
  syncConfidentialJobs: boolean;
  onClose: () => void;
  onConnect: () => void;
}

const AshbyDialog: FC<Props> = ({ apiKey, syncConfidentialJobs, onClose, onConnect }) => {
  const { t } = useTranslation();
  const atsService = getAtsService(AtsType.Ashby);

  const [apiKeyVal, setAPIKeyVal] = useState<string>(apiKey);
  const [syncConfidentialJobsVal, setSyncConfidentialJobsVal] = useState<boolean>(syncConfidentialJobs);

  const { isLoading } = useAshbyIntegration({
    onSuccess(res) {
      setAPIKeyVal(res?.api_key || '');
      setSyncConfidentialJobsVal(res?.sync_confidential_jobs || false);
    },
  });

  const [error, setError] = useState<string>();
  const putAshby = useAshbyIntegrationPut();

  const handleOnClose = useCallback(() => {
    setError('');
    onClose();
  }, [onClose]);

  const onSave = useCallback(() => {
    putAshby.mutate(
      { apiKey: apiKeyVal, syncConfidentialJobs: syncConfidentialJobsVal },
      {
        onError: (err) => {
          if (err) {
            setError(t('hiring-dashboard-settings-integrations:settings.integrations.ashbyDialog.invalidApiKey'));
          }
        },
        onSuccess: () => {
          handleOnClose();
          onConnect();
        },
      }
    );
  }, [apiKeyVal, syncConfidentialJobsVal, handleOnClose, onConnect, putAshby, t]);

  return (
    <Dialog
      open
      onClose={handleOnClose}
      title={t('hiring-dashboard-settings-integrations:settings.integrations.dialogTitle.ashby')}
      subTitle={t('hiring-dashboard-settings-integrations:settings.integrations.ashbyDialog.manageThisIntegration')}
      submitOptions={{
        onClick: onSave,
        label: t('hiring-dashboard-common:common.save'),
      }}
      cancelOptions={{
        label: t('hiring-dashboard-common:common.cancel'),
        onClick: handleOnClose,
      }}
      decoration={
        <img
          style={{ borderRadius: '6px' }}
          height="64px"
          width="64px"
          alt={t('hiring-dashboard-settings-integrations:settings.integrations.apiKeyDialog.logoAlt')}
          src={atsService.logoUrl}
        />
      }
    >
      {isLoading && <Loader loading={isLoading} />}
      {!isLoading && (
        <Stack direction="column" gap={2.5}>
          {/* API Key section */}
          <Stack direction="column" justifyContent="space-between" gap={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight={600}>
                {t('hiring-dashboard-settings-integrations:settings.integrations.apiKeyDialog.apiKeyLabel')}
              </Typography>
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href="https://app.ashbyhq.com/admin/api/keys"
                color="primary"
              >
                <Typography variant="body2">
                  {t('hiring-dashboard-settings-integrations:settings.integrations.ashbyDialog.viewInAshby')}
                </Typography>
              </Link>
            </Stack>
            <TextField
              fullWidth
              id="outlined-basic"
              variant="outlined"
              value={apiKeyVal}
              onChange={(event) => setAPIKeyVal(event.target.value)}
            />
          </Stack>

          {/* Confidential data section */}
          <Stack direction="column" justifyContent="space-between" gap={1}>
            <Typography fontWeight={600}>
              {t('hiring-dashboard-settings-integrations:settings.integrations.ashbyDialog.confidentialData')}
            </Typography>
            <FormControlLabel
              labelPlacement="start"
              sx={{
                justifyContent: 'space-between',
              }}
              checked={syncConfidentialJobsVal}
              control={<Switch />}
              label={t('hiring-dashboard-settings-integrations:settings.integrations.ashbyDialog.syncConfidentialJobs')}
              onChange={(_, checked) => setSyncConfidentialJobsVal(checked)}
            />
            <Typography color="text.secondary">
              {t(
                'hiring-dashboard-settings-integrations:settings.integrations.ashbyDialog.confidentialDataDescription'
              )}
            </Typography>
          </Stack>
          <PublicError error={error} />
          <Link target="_blank" rel="noopener noreferrer" href={HelpCenterUrl.ASHBY_INTEGRATION} color="primary">
            <Typography>
              {t('hiring-dashboard-settings-integrations:settings.integrations.ashbyDialog.learnMore')}
            </Typography>
          </Link>
        </Stack>
      )}
    </Dialog>
  );
};

export default AshbyDialog;
